import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Dispatch, SetStateAction } from "react";
import { MenuItem } from "@/components/design/MenuItem";
import type { DocumentFilterDefinition, DocumentFilterKey } from "./types";

export function DocumentFilterBuilder({
  filters,
  selected,
  setSelected,
}: {
  filters: readonly DocumentFilterDefinition[];
  selected: readonly DocumentFilterKey[];
  setSelected: Dispatch<SetStateAction<DocumentFilterKey[]>>;
}) {
  const intl = useIntl();
  const [editingKey, setEditingKey] = useState<DocumentFilterKey | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const displayedFilters = selected
    .map((key) => filters.find((filter) => filter.key === key))
    .filter((filter): filter is DocumentFilterDefinition => Boolean(filter));
  const availableFilters = filters.filter((filter) => !selected.includes(filter.key));

  const addFilter = (key: DocumentFilterKey) => {
    setSelected((current) => (current.includes(key) ? current : [...current, key]));
    setEditingKey(key);
    setAddOpen(false);
  };

  const removeFilter = (filter: DocumentFilterDefinition) => {
    filter.onChange("all");
    setSelected((current) => current.filter((key) => key !== filter.key));
    setEditingKey((key) => (key === filter.key ? null : key));
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        {displayedFilters.map((filter) => {
          const value = filter.value !== "all" ? filter.value : null;
          const editing = editingKey === filter.key;
          return (
            <span
              key={filter.key}
              className={`relative inline-flex items-center rounded-full py-1 pl-2.5 pr-1 text-[11px] transition ${
                value
                  ? `bg-surface text-ink/80 ring-1 ${editing ? "ring-accent/50" : "ring-ink/15"}`
                  : `text-ink/80 ${editing ? "ring-1 ring-accent/50" : "border border-dashed border-ink/30"}`
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  setEditingKey(filter.key);
                  setAddOpen(false);
                }}
                className="inline-flex min-w-0 items-center gap-1"
              >
                <span className="text-ink/70">
                  {filter.label}
                  {value ? ":" : ""}
                </span>
                {value ? <span className="max-w-[180px] truncate text-ink/80">{value}</span> : null}
              </button>
              <button
                type="button"
                aria-label={intl.formatMessage(
                  { id: "service.filterBuilder.removeFilterLabel", defaultMessage: "Remove {label}" },
                  { label: filter.label },
                )}
                onClick={() => removeFilter(filter)}
                className="ml-1 grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink/55 transition hover:bg-ink/10 hover:text-ink"
              >
                <span className="block text-[13px] leading-none">×</span> {/* i18n-allow */}
              </button>
              {editing ? (
                // Option picker as a popover anchored to the chip - keeps the
                // toolbar a single calm line instead of expanding a panel.
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setEditingKey(null)} />
                  <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-md bg-surface p-3 shadow-lg ring-1 ring-ink/10">
                    <div className="field-label mb-1.5 text-ink/80">{filter.label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => filter.onChange("all")}
                        className={`rounded-full px-2.5 py-1 text-[11px] ring-1 transition ${
                          filter.value === "all"
                            ? "bg-ink/[0.06] font-medium text-ink ring-ink/30"
                            : "bg-surface text-ink/80 ring-ink/10 hover:text-ink"
                        }`}
                      >
                        <FormattedMessage id="service.filterBuilder.all" defaultMessage="All" />
                      </button>
                      {filter.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => filter.onChange(option)}
                          className={`rounded-full px-2.5 py-1 text-[11px] ring-1 transition ${
                            filter.value === option
                              ? "bg-ink/[0.06] font-medium text-ink ring-ink/30"
                              : "bg-surface text-ink/80 ring-ink/10 hover:text-ink"
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-3">
                      <button
                        type="button"
                        onClick={() => filter.onChange("all")}
                        className="field-label text-ink/70 hover:text-accent"
                      >
                        <FormattedMessage id="service.filterBuilder.clear" defaultMessage="Clear" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingKey(null)}
                        className="field-label text-ink/70 hover:text-accent"
                      >
                        <FormattedMessage id="service.filterBuilder.done" defaultMessage="Done" />
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </span>
          );
        })}

        <div className="relative">
          <button
            type="button"
            disabled={availableFilters.length === 0}
            onClick={() => {
              setAddOpen((open) => !open);
              setEditingKey(null);
            }}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] text-ink/80 ring-1 ring-ink/15 transition hover:bg-surface hover:text-ink disabled:opacity-40"
          >
            <FormattedMessage id="service.filterBuilder.addFilter" defaultMessage="+ Add filter" />
          </button>
          {addOpen ? (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setAddOpen(false)} />
              <div className="absolute left-0 top-full z-30 mt-1 w-48 overflow-auto rounded-md bg-surface py-1 shadow-lg ring-1 ring-ink/10">
                {availableFilters.length === 0 ? (
                  <div className="px-3 py-1.5 text-[12px] text-ink/55">
                    <FormattedMessage
                      id="service.filterBuilder.allFiltersAdded"
                      defaultMessage="All filters added"
                    />
                  </div>
                ) : (
                  availableFilters.map((filter) => (
                    <MenuItem
                      key={filter.key}
                      onClick={() => addFilter(filter.key)}
                    >
                      {filter.label}
                    </MenuItem>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
